import { Link } from 'react-router-dom';
import { useState, useEffect, useMemo } from 'react';
import { 
  DollarSign,
  BookOpen,
  Users,
  BarChart3,
  ArrowRight,
  HardDrive,
  Palette,
  Shield,
  UserPlus,
  Settings,
  TrendingUp,
  Award,
  Zap,
  Globe,
  Package,
  Crown,
  Sparkles,
  Target,
  Trophy,
  Lightbulb,
  Rocket,
  MessageCircle,
  FileText,
  ExternalLink,
  Plus
} from 'lucide-react';
import { useAuthContext } from '../contexts/AuthContext';
import { useSubscription } from '../hooks/useSubscription';
import { useAccessControl } from '../hooks/useAccessControl';
import AccessBlockBanner from './AccessBlockBanner';
import VideoReportsInstructor from './VideoReportsInstructor';
import { ChartBarIcon } from '../icons/OutlineIcons';
import { InstructorStats } from '../services/instructorStatsService';
import ModeratorManagement from './ModeratorManagement';
import StatCard from './StatCard';
import Card from './Card';
import CrossProductUpgradeCard from './CrossProductUpgradeCard';
import InstructorFeatureGuard from './InstructorFeatureGuard';
import {
  AnalyticsRange,
  CompletionPoint,
  CourseAnalytics,
  InstructorAnalyticsSummary,
  RevenuePoint,
  TopCourseSalesPoint,
  loadInstructorAnalytics,
} from '../services/analyticsService';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  BarChart,
  Bar,
} from 'recharts';

interface DashboardContentProps {
  activeSection: string;
  stats: InstructorStats | null;
  user: any;
  plan: any;
  videoInfo: any;
  fileInfo: any;
  isPro: boolean;
  lastUpdated: string;
  emailVerified: boolean;
  onSectionChange: (section: string) => void;
  hasAccess?: boolean;
  blockMessage?: string;
  blockReason?: any;
}

export default function DashboardContent({
  activeSection,
  stats,
  user,
  plan,
  videoInfo,
  fileInfo,
  isPro,
  lastUpdated,
  emailVerified,
  onSectionChange,
  hasAccess: hasAccessProp,
  blockMessage: blockMessageProp,
  blockReason: blockReasonProp,
}: DashboardContentProps) {
  
  // Only call useAccessControl if props are not provided
  const shouldUseHook = hasAccessProp === undefined;
  const { hasAccess: hasAccessHook, blockReason: blockReasonHook, blockMessage: blockMessageHook, isLoading } = shouldUseHook ? useAccessControl() : { hasAccess: true, blockReason: null, blockMessage: '', isLoading: false };
  
  // Use props if provided, otherwise use hook values
  const hasAccess = hasAccessProp !== undefined ? hasAccessProp : hasAccessHook;
  const blockMessage = blockMessageProp || blockMessageHook;
  const blockReason = blockReasonProp !== undefined ? blockReasonProp : blockReasonHook;
  
  const renderOverview = () => (
    <div className="space-y-6">
      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4 lg:gap-6">
        {/* Revenue Card */}
        <div className="bg-gradient-to-br from-green-500 to-emerald-600 rounded-xl md:rounded-2xl p-3 md:p-4 lg:p-6 text-white shadow-large hover:shadow-xl transition-all duration-300 transform hover:scale-105">
          <div className="flex items-center justify-between mb-2 md:mb-3">
            <div className="w-8 h-8 md:w-10 md:h-10 lg:w-12 lg:h-12 bg-white/20 rounded-lg md:rounded-xl flex items-center justify-center backdrop-blur-sm">
              <DollarSign className="w-4 h-4 md:w-5 md:h-5 lg:w-6 lg:h-6 text-white" />
            </div>
            <span className="text-[10px] md:text-xs bg-white/20 px-1.5 md:px-2 py-0.5 md:py-1 rounded-full backdrop-blur-sm">
              {isPro && <Zap className="w-2.5 h-2.5 md:w-3 md:h-3 inline" />}
            </span>
          </div>
          <p className="text-[10px] md:text-xs opacity-90 mb-1">รายได้รวม</p>
          <p className="text-xl md:text-2xl lg:text-3xl font-bold">฿{(stats?.totalRevenue || 0).toLocaleString()}</p>
          {isPro && (
            <p className="text-[9px] md:text-xs opacity-75 mt-1 md:mt-2">ไม่หักค่าธรรมเนียม 100%</p>
          )}
        </div>

        {/* Courses Card */}
        <div className="bg-gradient-to-br from-blue-500 to-pink-600 rounded-xl md:rounded-2xl p-3 md:p-4 lg:p-6 text-white shadow-large hover:shadow-xl transition-all duration-300 transform hover:scale-105">
          <div className="flex items-center justify-between mb-2 md:mb-3">
            <div className="w-8 h-8 md:w-10 md:h-10 lg:w-12 lg:h-12 bg-white/20 rounded-lg md:rounded-xl flex items-center justify-center backdrop-blur-sm">
              <BookOpen className="w-4 h-4 md:w-5 md:h-5 lg:w-6 lg:h-6 text-white" />
            </div>
            <span className="text-[10px] md:text-xs bg-white/20 px-1.5 md:px-2 py-0.5 md:py-1 rounded-full backdrop-blur-sm">
              Active
            </span>
          </div>
          <p className="text-[10px] md:text-xs opacity-90 mb-1">จำนวนคอร์ส</p>
          <p className="text-xl md:text-2xl lg:text-3xl font-bold">{stats?.totalCourses || 0}</p>
          {isPro && (
            <p className="text-[9px] md:text-xs opacity-75 mt-1 md:mt-2">ไม่จำกัดจำนวน</p>
          )}
        </div>

        {/* Students Card */}
        <div className="bg-gradient-to-br from-blue-500 to-cyan-600 rounded-xl md:rounded-2xl p-3 md:p-4 lg:p-6 text-white shadow-large hover:shadow-xl transition-all duration-300 transform hover:scale-105">
          <div className="flex items-center justify-between mb-2 md:mb-3">
            <div className="w-8 h-8 md:w-10 md:h-10 lg:w-12 lg:h-12 bg-white/20 rounded-lg md:rounded-xl flex items-center justify-center backdrop-blur-sm">
              <Users className="w-4 h-4 md:w-5 md:h-5 lg:w-6 lg:h-6 text-white" />
            </div>
            <span className="text-[10px] md:text-xs bg-white/20 px-1.5 md:px-2 py-0.5 md:py-1 rounded-full backdrop-blur-sm">
              Total
            </span>
          </div>
          <p className="text-[10px] md:text-xs opacity-90 mb-1">นักเรียนทั้งหมด</p>
          <p className="text-xl md:text-2xl lg:text-3xl font-bold">{stats?.totalStudents || 0}</p>
          <p className="text-[9px] md:text-xs opacity-75 mt-1 md:mt-2">ไม่จำกัดจำนวน</p>
        </div>

        {/* Storage Card - Pro Only */}
        {isPro && (
          <div className="bg-gradient-to-br from-blue-500 to-red-600 rounded-xl md:rounded-2xl p-3 md:p-4 lg:p-6 text-white shadow-large hover:shadow-xl transition-all duration-300 transform hover:scale-105">
            <div className="flex items-center justify-between mb-2 md:mb-3">
              <div className="w-8 h-8 md:w-10 md:h-10 lg:w-12 lg:h-12 bg-white/20 rounded-lg md:rounded-xl flex items-center justify-center backdrop-blur-sm">
                <HardDrive className="w-4 h-4 md:w-5 md:h-5 lg:w-6 lg:h-6 text-white" />
              </div>
              <span className="text-[10px] md:text-xs bg-white/20 px-1.5 md:px-2 py-0.5 md:py-1 rounded-full backdrop-blur-sm">
                {videoInfo ? `${videoInfo.total.toFixed(1)} GB` : '20 GB'}
              </span>
            </div>
            <p className="text-[10px] md:text-xs opacity-90 mb-1">พื้นที่จัดเก็บ</p>
            <p className="text-xl md:text-2xl lg:text-3xl font-bold">
              {videoInfo ? `${videoInfo.used.toFixed(1)} GB` : '0 GB'}
            </p>
            <p className="text-[9px] md:text-xs opacity-75 mt-1 md:mt-2">+ Add-ons ได้</p>
          </div>
        )}
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-4 lg:gap-6">
        {/* Manage Courses Card */}
        {hasAccess ? (
          <Link to="/instructor/my-courses" className="touch-target">
            <div className="bg-white rounded-xl md:rounded-2xl p-4 md:p-6 shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:scale-105 border-2 border-blue-100 hover:border-blue-300">
              <div className="flex items-start justify-between mb-2 md:mb-3">
                <div className="w-10 h-10 md:w-12 md:h-12 lg:w-14 lg:h-14 bg-gradient-to-br from-blue-500 to-cyan-600 rounded-lg md:rounded-xl flex items-center justify-center">
                  <Settings className="w-5 h-5 md:w-6 md:h-6 lg:w-7 lg:h-7 text-white" />
                </div>
                <ArrowRight className="w-4 h-4 md:w-5 md:h-5 text-blue-600" />
              </div>
              <h3 className="text-base md:text-lg lg:text-xl font-bold text-textDark mb-1 md:mb-2">จัดการคอร์ส</h3>
              <p className="text-xs md:text-sm text-textLight mb-2 md:mb-3">แก้ไขและจัดการคอร์สที่มีอยู่</p>
              <div className="flex items-center gap-1.5 md:gap-2">
                <span className="text-lg md:text-xl lg:text-2xl font-bold text-primary">{stats?.totalCourses || 0}</span>
                <span className="text-xs md:text-sm text-textLight">คอร์ส</span>
              </div>
            </div>
          </Link>
        ) : (
          <div className="touch-target cursor-not-allowed opacity-60 relative group">
            <div className="bg-gray-100 rounded-xl md:rounded-2xl p-4 md:p-6 shadow-md border-2 border-gray-200">
              <div className="flex items-start justify-between mb-2 md:mb-3">
                <div className="w-10 h-10 md:w-12 md:h-12 lg:w-14 lg:h-14 bg-gray-300 rounded-lg md:rounded-xl flex items-center justify-center">
                  <Settings className="w-5 h-5 md:w-6 md:h-6 lg:w-7 lg:h-7 text-gray-500" />
                </div>
              </div>
              <h3 className="text-base md:text-lg lg:text-xl font-bold text-gray-600 mb-1 md:mb-2">จัดการคอร์ส</h3>
              <p className="text-xs md:text-sm text-gray-500 mb-2 md:mb-3">แก้ไขและจัดการคอร์สที่มีอยู่</p>
              <div className="flex items-center gap-1.5 md:gap-2">
                <span className="text-lg md:text-xl lg:text-2xl font-bold text-gray-500">{stats?.totalCourses || 0}</span>
                <span className="text-xs md:text-sm text-gray-500">คอร์ส</span>
              </div>
            </div>
            <div className="absolute inset-0 flex items-center justify-center bg-black/5 rounded-xl md:rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity">
              <div className="bg-gray-900 text-white text-xs px-3 py-2 rounded-lg shadow-lg">
                {blockMessage}
              </div>
            </div>
          </div>
        )}

        {/* Create Course Card */}
        {hasAccess ? (
          <Link to="/instructor/courses/new" className="touch-target">
            <div className="bg-white rounded-xl md:rounded-2xl p-4 md:p-6 shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:scale-105 border-2 border-blue-100 hover:border-blue-300">
              <div className="flex items-start justify-between mb-2 md:mb-3">
                <div className="w-10 h-10 md:w-12 md:h-12 lg:w-14 lg:h-14 bg-gradient-to-br from-blue-500 to-pink-600 rounded-lg md:rounded-xl flex items-center justify-center">
                  <BookOpen className="w-5 h-5 md:w-6 md:h-6 lg:w-7 lg:h-7 text-white" />
                </div>
                <ArrowRight className="w-4 h-4 md:w-5 md:h-5 text-blue-600" />
              </div>
              <h3 className="text-base md:text-lg lg:text-xl font-bold text-textDark mb-1 md:mb-2">สร้างคอร์สใหม่</h3>
              <p className="text-xs md:text-sm text-textLight mb-2 md:mb-3">เริ่มสร้างคอร์สเรียนออนไลน์ของคุณ</p>
              <div className="text-xs md:text-sm text-blue-600 font-semibold">เริ่มสร้าง →</div>
            </div>
          </Link>
        ) : (
          <div className="touch-target cursor-not-allowed opacity-60 relative group">
            <div className="bg-gray-100 rounded-xl md:rounded-2xl p-4 md:p-6 shadow-md border-2 border-gray-200">
              <div className="flex items-start justify-between mb-2 md:mb-3">
                <div className="w-10 h-10 md:w-12 md:h-12 lg:w-14 lg:h-14 bg-gray-300 rounded-lg md:rounded-xl flex items-center justify-center">
                  <BookOpen className="w-5 h-5 md:w-6 md:h-6 lg:w-7 lg:h-7 text-gray-500" />
                </div>
              </div>
              <h3 className="text-base md:text-lg lg:text-xl font-bold text-gray-600 mb-1 md:mb-2">สร้างคอร์สใหม่</h3>
              <p className="text-xs md:text-sm text-gray-500 mb-2 md:mb-3">เริ่มสร้างคอร์สเรียนออนไลน์ของคุณ</p>
              <div className="text-xs md:text-sm text-gray-500 font-semibold">ไม่สามารถใช้งานได้</div>
            </div>
            <div className="absolute inset-0 flex items-center justify-center bg-black/5 rounded-xl md:rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity">
              <div className="bg-gray-900 text-white text-xs px-3 py-2 rounded-lg shadow-lg">
                {blockMessage}
              </div>
            </div>
          </div>
        )}

        {/* Discount Management Card */}
        {hasAccess ? (
          <Link to="/instructor/discounts" className="touch-target">
            <div className="bg-white rounded-xl md:rounded-2xl p-4 md:p-6 shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:scale-105 border-2 border-blue-100 hover:border-blue-300">
              <div className="flex items-start justify-between mb-2 md:mb-3">
                <div className="w-10 h-10 md:w-12 md:h-12 lg:w-14 lg:h-14 bg-gradient-to-br from-blue-500 to-cyan-600 rounded-lg md:rounded-xl flex items-center justify-center">
                  <BarChart3 className="w-5 h-5 md:w-6 md:h-6 lg:w-7 lg:h-7 text-white" />
                </div>
                <ArrowRight className="w-4 h-4 md:w-5 md:h-5 text-blue-600" />
              </div>
              <h3 className="text-base md:text-lg lg:text-xl font-bold text-textDark mb-1 md:mb-2">จัดการส่วนลด</h3>
              <p className="text-xs md:text-sm text-textLight mb-2 md:mb-3">สร้างและจัดการโค้ดส่วนลดสำหรับโปรโมชั่น</p>
              <div className="flex items-center gap-1.5 md:gap-2">
                <TrendingUp className="w-3.5 h-3.5 md:w-4 md:h-4 text-blue-600" />
                <span className="text-xs md:text-sm text-blue-600 font-semibold">ดูข้อมูล →</span>
              </div>
            </div>
          </Link>
        ) : (
          <div className="touch-target cursor-not-allowed opacity-60 relative group">
            <div className="bg-gray-100 rounded-xl md:rounded-2xl p-4 md:p-6 shadow-md border-2 border-gray-200">
              <div className="flex items-start justify-between mb-2 md:mb-3">
                <div className="w-10 h-10 md:w-12 md:h-12 lg:w-14 lg:h-14 bg-gray-300 rounded-lg md:rounded-xl flex items-center justify-center">
                  <BarChart3 className="w-5 h-5 md:w-6 md:h-6 lg:w-7 lg:h-7 text-gray-500" />
                </div>
              </div>
              <h3 className="text-base md:text-lg lg:text-xl font-bold text-gray-600 mb-1 md:mb-2">จัดการส่วนลด</h3>
              <p className="text-xs md:text-sm text-gray-500 mb-2 md:mb-3">สร้างและจัดการโค้ดส่วนลดสำหรับโปรโมชั่น</p>
              <div className="flex items-center gap-1.5 md:gap-2">
                <TrendingUp className="w-3.5 h-3.5 md:w-4 md:h-4 text-gray-500" />
                <span className="text-xs md:text-sm text-gray-500 font-semibold">ไม่สามารถใช้งานได้</span>
              </div>
            </div>
            <div className="absolute inset-0 flex items-center justify-center bg-black/5 rounded-xl md:rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity">
              <div className="bg-gray-900 text-white text-xs px-3 py-2 rounded-lg shadow-lg">
                {blockMessage}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Cross-Product Upgrade Card */}
      <div className="mt-8">
        <CrossProductUpgradeCard type="salepage" />
      </div>
    </div>
  );

  const renderRevenue = () => (
    <div className="space-y-6">
      <div className="bg-white rounded-2xl p-6 shadow-xl">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-emerald-600 rounded-xl flex items-center justify-center">
              <DollarSign className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-textDark">รายได้</h2>
              <p className="text-sm text-textLight">สรุปรายได้ทั้งหมด</p>
            </div>
          </div>
          <button 
            onClick={() => onSectionChange('analytics')}
            className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm"
          >
            <BarChart3 className="w-4 h-4" />
            ดูกราฟรายได้
          </button>
        </div>
        
        <div className="space-y-4">
          <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl p-6 border border-green-200">
            <div className="flex justify-between items-center">
              <div>
                <p className="text-sm text-gray-600 mb-1">รายได้รวม</p>
                <p className="text-3xl font-bold text-green-600">฿{(stats?.totalRevenue || 0).toLocaleString()}</p>
              </div>
              {isPro && (
                <div className="text-right">
                  <p className="text-xs text-gray-500">ไม่หักค่าธรรมเนียม</p>
                  <p className="text-sm font-semibold text-green-600">100%</p>
                </div>
              )}
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-blue-50 rounded-xl p-4">
              <p className="text-sm text-gray-600 mb-1">รายได้รายเดือน (30 วัน)</p>
              <p className="text-xl font-bold text-blue-600">฿{(stats?.monthlyRevenue || 0).toLocaleString()}</p>
            </div>
            <div className="bg-blue-50 rounded-xl p-4">
              <p className="text-sm text-gray-600 mb-1">คอร์สที่ขายได้</p>
              <p className="text-xl font-bold text-blue-600">{stats?.totalCourses || 0} คอร์ส</p>
            </div>
          </div>
        </div>
      </div>

      {/* Quick Info */}
      <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl p-6 border border-blue-200">
        <div className="flex items-start gap-3">
          <TrendingUp className="w-6 h-6 text-blue-600 flex-shrink-0 mt-1" />
          <div>
            <h3 className="font-bold text-textDark mb-2">ดูกราฟรายได้แบบละเอียด</h3>
            <p className="text-sm text-textLight mb-4">
              ไปที่หน้า Analytics เพื่อดูกราฟรายได้ตามช่วงเวลา, อัตราจบคอร์ส, และสถิติคอร์สยอดนิยม
            </p>
            <button 
              onClick={() => onSectionChange('analytics')}
              className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-700 font-semibold text-sm"
            >
              ไปที่ Analytics →
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  const renderCourses = () => (
    <div className="bg-white rounded-2xl p-6 shadow-xl">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-pink-600 rounded-xl flex items-center justify-center">
          <BookOpen className="w-6 h-6 text-white" />
        </div>
        <div>
          <h2 className="text-2xl font-bold text-textDark">คอร์ส</h2>
          <p className="text-sm text-textLight">จำนวนคอร์สทั้งหมด</p>
        </div>
      </div>
      
      <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl p-6 border border-blue-200">
        <div className="flex justify-between items-center">
          <div>
            <p className="text-sm text-gray-600 mb-1">จำนวนคอร์ส</p>
            <p className="text-3xl font-bold text-blue-600">{stats?.totalCourses || 0} คอร์ส</p>
          </div>
          {isPro && (
            <div className="text-right">
              <p className="text-xs text-gray-500">ไม่จำกัดจำนวน</p>
              <p className="text-sm font-semibold text-blue-600">Unlimited</p>
            </div>
          )}
        </div>
      </div>
      
      <div className="mt-6 flex flex-wrap gap-3">
        <InstructorFeatureGuard>
          <Link 
            to="/instructor/my-courses"
            className="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <BookOpen className="w-5 h-5" />
            จัดการคอร์ส
          </Link>
        </InstructorFeatureGuard>
        <InstructorFeatureGuard>
          <Link 
            to="/instructor/courses/new"
            className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-blue-500 to-cyan-500 text-white rounded-lg hover:from-blue-600 hover:to-cyan-600 transition-colors"
          >
            <BookOpen className="w-5 h-5" />
            สร้างคอร์สใหม่
          </Link>
        </InstructorFeatureGuard>
      </div>
    </div>
  );

  const renderStudents = () => (
    <div className="bg-white rounded-2xl p-6 shadow-xl">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-cyan-600 rounded-xl flex items-center justify-center">
          <Users className="w-6 h-6 text-white" />
        </div>
        <div>
          <h2 className="text-2xl font-bold text-textDark">นักเรียน</h2>
          <p className="text-sm text-textLight">นักเรียนทั้งหมด</p>
        </div>
      </div>
      
      <div className="space-y-4">
        <div className="bg-gradient-to-r from-blue-50 to-cyan-50 rounded-xl p-6 border border-blue-200">
          <div className="flex justify-between items-center">
            <div>
              <p className="text-sm text-gray-600 mb-1">นักเรียนทั้งหมด</p>
              <p className="text-3xl font-bold text-blue-600">{stats?.totalStudents || 0} คน</p>
            </div>
            <div className="text-right">
              <p className="text-xs text-gray-500">ไม่จำกัดจำนวน</p>
              <p className="text-sm font-semibold text-blue-600">Unlimited</p>
            </div>
          </div>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-green-50 rounded-xl p-4">
            <p className="text-sm text-gray-600 mb-1">เสร็จสิ้นวันนี้</p>
            <p className="text-xl font-bold text-green-600">{stats?.dailyCompletions || 0} คน</p>
          </div>
          <div className="bg-blue-50 rounded-xl p-4">
            <p className="text-sm text-gray-600 mb-1">เสร็จสิ้นสัปดาห์นี้</p>
            <p className="text-xl font-bold text-blue-600">{stats?.weeklyCompletions || 0} คน</p>
          </div>
        </div>
      </div>
      
      <div className="mt-6 flex gap-4">
        {hasAccess ? (
          <Link 
            to="/instructor/students"
            className="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Users className="w-5 h-5" />
            ดูรายงานนักเรียน
          </Link>
        ) : (
          <button 
            disabled
            className="inline-flex items-center gap-2 px-6 py-3 bg-gray-300 text-gray-500 rounded-lg cursor-not-allowed opacity-60"
            title={blockMessage}
          >
            <Users className="w-5 h-5" />
            ดูรายงานนักเรียน
          </button>
        )}
        {hasAccess ? (
          <Link 
            to="/instructor/approve-students"
            className="inline-flex items-center gap-2 px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
          >
            <BookOpen className="w-5 h-5" />
            อนุมัตินักเรียน
          </Link>
        ) : (
          <button 
            disabled
            className="inline-flex items-center gap-2 px-6 py-3 bg-gray-300 text-gray-500 rounded-lg cursor-not-allowed opacity-60"
            title={blockMessage}
          >
            <BookOpen className="w-5 h-5" />
            อนุมัตินักเรียน
          </button>
        )}
      </div>
    </div>
  );

  const renderStorage = () => (
    <div className="space-y-6">
      <div className="bg-white rounded-2xl p-6 shadow-xl">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-red-600 rounded-xl flex items-center justify-center">
            <HardDrive className="w-6 h-6 text-white" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-textDark">พื้นที่จัดเก็บ</h2>
            <p className="text-sm text-textLight">{videoInfo ? `${Math.round((videoInfo.total / 1024) * 10) / 10} GB` : '0 GB'} พื้นฐาน + Add-ons</p>
          </div>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Video Storage */}
          <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-gray-700">วิดีโอ</span>
              <span className="text-sm font-bold text-blue-600">
                {videoInfo ? `${Math.round((videoInfo.used / 1024) * 10) / 10} / ${Math.round((videoInfo.total / 1024) * 10) / 10} GB` : '0 / 0 GB'}
              </span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-3">
              <div 
                className="bg-gradient-to-r from-blue-500 to-pink-600 h-3 rounded-full transition-all"
                style={{ width: `${videoInfo ? Math.min(videoInfo.percentage, 100) : 0}%` }}
              />
            </div>
          </div>
          {/* File Storage */}
          <div className="bg-gradient-to-br from-blue-50 to-cyan-50 rounded-xl p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-gray-700">ไฟล์เอกสาร</span>
              <span className="text-sm font-bold text-blue-600">
                {fileInfo ? `${Math.round((fileInfo.used / 1024) * 10) / 10} / ${Math.round((fileInfo.total / 1024) * 10) / 10} GB` : '0 / 0 GB'}
              </span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-3">
              <div 
                className="bg-gradient-to-r from-blue-500 to-cyan-600 h-3 rounded-full transition-all"
                style={{ width: `${fileInfo ? Math.min(fileInfo.percentage, 100) : 0}%` }}
              />
            </div>
          </div>
        </div>
        
        <div className="mt-6">
          {hasAccess ? (
            <Link 
              to="/instructor/storage"
              className="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <HardDrive className="w-5 h-5" />
              จัดการพื้นที่จัดเก็บ
            </Link>
          ) : (
            <button 
              disabled
              className="inline-flex items-center gap-2 px-6 py-3 bg-gray-300 text-gray-500 rounded-lg cursor-not-allowed opacity-60"
              title={blockMessage}
            >
              <HardDrive className="w-5 h-5" />
              จัดการพื้นที่จัดเก็บ
            </button>
          )}
        </div>
      </div>
    </div>
  );

  const renderApproveStudents = () => (
    <div className="bg-white rounded-2xl p-6 shadow-xl">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-12 h-12 bg-gradient-primary rounded-xl flex items-center justify-center">
          <BookOpen className="w-6 h-6 text-white" />
        </div>
        <div>
          <h2 className="text-2xl font-bold text-textDark">อนุมัตินักเรียน</h2>
          <p className="text-sm text-textLight">ตรวจสอบและอนุมัติการชำระเงิน</p>
        </div>
      </div>
      
      <div className="text-center py-12">
        <BookOpen className="w-16 h-16 text-blue-600 mx-auto mb-4" />
        <p className="text-gray-600 mb-6">ตรวจสอบและอนุมัติการชำระเงินภายใน 24 ชม.</p>
        {hasAccess ? (
          <Link 
            to="/instructor/approve-students"
            className="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <BookOpen className="w-5 h-5" />
            ไปยังหน้าอนุมัตินักเรียน
          </Link>
        ) : (
          <button 
            disabled
            className="inline-flex items-center gap-2 px-6 py-3 bg-gray-300 text-gray-500 rounded-lg cursor-not-allowed opacity-60"
            title={blockMessage}
          >
            <BookOpen className="w-5 h-5" />
            ไปยังหน้าอนุมัตินักเรียน
          </button>
        )}
      </div>
    </div>
  );

  const renderStudentReports = () => (
    <div className="bg-white rounded-2xl p-6 shadow-xl">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-emerald-600 rounded-xl flex items-center justify-center">
          <Users className="w-6 h-6 text-white" />
        </div>
        <div>
          <h2 className="text-2xl font-bold text-textDark">รายงานนักเรียน</h2>
          <p className="text-sm text-textLight">ติดตามความคืบหน้าของนักเรียน</p>
        </div>
      </div>
      
      <div className="text-center py-12">
        <Users className="w-16 h-16 text-green-600 mx-auto mb-4" />
        <p className="text-gray-600 mb-6">ดูรายงานความคืบหน้าและสถิติของนักเรียน</p>
        {hasAccess ? (
          <Link 
            to="/instructor/students"
            className="inline-flex items-center gap-2 px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
          >
            <Users className="w-5 h-5" />
            ดูรายงาน
          </Link>
        ) : (
          <button 
            disabled
            className="inline-flex items-center gap-2 px-6 py-3 bg-gray-300 text-gray-500 rounded-lg cursor-not-allowed opacity-60"
            title={blockMessage}
          >
            <Users className="w-5 h-5" />
            ดูรายงาน
          </button>
        )}
      </div>
    </div>
  );

  const renderDiscounts = () => (
    <div className="bg-white rounded-2xl p-6 shadow-xl">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-cyan-600 rounded-xl flex items-center justify-center">
          <BarChart3 className="w-6 h-6 text-white" />
        </div>
        <div>
          <h2 className="text-2xl font-bold text-textDark">จัดการส่วนลด</h2>
          <p className="text-sm text-textLight">สร้างและจัดการโค้ดส่วนลด</p>
        </div>
      </div>
      
      <div className="text-center py-12">
        <BarChart3 className="w-16 h-16 text-blue-600 mx-auto mb-4" />
        <p className="text-gray-600 mb-6">สร้างและจัดการโค้ดส่วนลดสำหรับโปรโมชั่น</p>
        {hasAccess ? (
          <Link 
            to="/instructor/discounts"
            className="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <BarChart3 className="w-5 h-5" />
            จัดการส่วนลด
          </Link>
        ) : (
          <button 
            disabled
            className="inline-flex items-center gap-2 px-6 py-3 bg-gray-300 text-gray-500 rounded-lg cursor-not-allowed opacity-60"
            title={blockMessage}
          >
            <BarChart3 className="w-5 h-5" />
            จัดการส่วนลด
          </button>
        )}
      </div>
    </div>
  );

  const renderTeamManagement = () => (
    <div className="bg-white rounded-2xl p-6 shadow-xl">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-12 h-12 bg-gradient-to-br from-indigo-500 to-blue-600 rounded-xl flex items-center justify-center">
          <UserPlus className="w-6 h-6 text-white" />
        </div>
        <div>
          <h2 className="text-2xl font-bold text-textDark">จัดการทีม</h2>
          <p className="text-sm text-textLight">เพิ่มผู้สอนไม่จำกัด</p>
        </div>
      </div>
      
      <div className="text-center py-12">
        <UserPlus className="w-16 h-16 text-indigo-600 mx-auto mb-4" />
        <p className="text-gray-600 mb-6">เพิ่มผู้สอนในทีมของคุณ</p>
        {hasAccess ? (
          <Link 
            to="/instructor/team"
            className="inline-flex items-center gap-2 px-6 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
          >
            <UserPlus className="w-5 h-5" />
            จัดการทีม
          </Link>
        ) : (
          <button 
            disabled
            className="inline-flex items-center gap-2 px-6 py-3 bg-gray-300 text-gray-500 rounded-lg cursor-not-allowed opacity-60"
            title={blockMessage}
          >
            <UserPlus className="w-5 h-5" />
            จัดการทีม
          </button>
        )}
      </div>
    </div>
  );

  const renderWhiteLabel = () => (
    <div className="bg-white rounded-2xl p-6 shadow-xl">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-12 h-12 bg-gradient-to-br from-pink-500 to-rose-600 rounded-xl flex items-center justify-center">
          <Palette className="w-6 h-6 text-white" />
        </div>
        <div>
          <h2 className="text-2xl font-bold text-textDark">White Label</h2>
          <p className="text-sm text-textLight">ปรับแต่งธีมและโลโก้</p>
        </div>
      </div>
      
      <div className="text-center py-12">
        <Palette className="w-16 h-16 text-pink-600 mx-auto mb-4" />
        <p className="text-gray-600 mb-6">ปรับแต่งธีมและโลโก้ของคุณ</p>
        {hasAccess ? (
          <Link 
            to="/instructor/settings"
            className="inline-flex items-center gap-2 px-6 py-3 bg-pink-600 text-white rounded-lg hover:bg-pink-700 transition-colors"
          >
            <Palette className="w-5 h-5" />
            ตั้งค่า White Label
          </Link>
        ) : (
          <button 
            disabled
            className="inline-flex items-center gap-2 px-6 py-3 bg-gray-300 text-gray-500 rounded-lg cursor-not-allowed opacity-60"
            title={blockMessage}
          >
            <Palette className="w-5 h-5" />
            ตั้งค่า White Label
          </button>
        )}
      </div>
    </div>
  );

  const renderStorageAddons = () => (
    <div className="bg-white rounded-2xl p-6 shadow-xl">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-cyan-600 rounded-xl flex items-center justify-center">
          <Package className="w-6 h-6 text-white" />
        </div>
        <div>
          <h2 className="text-2xl font-bold text-textDark">Storage Add-ons</h2>
          <p className="text-sm text-textLight">เพิ่มพื้นที่ได้ไม่จำกัด</p>
        </div>
      </div>
      
      <div className="text-center py-12">
        <Package className="w-16 h-16 text-blue-600 mx-auto mb-4" />
        <p className="text-gray-600 mb-6">ซื้อพื้นที่เพิ่มเติมสำหรับคอร์สของคุณ</p>
        {hasAccess ? (
          <Link 
            to="/checkout/addon"
            className="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Package className="w-5 h-5" />
            ซื้อ Storage Add-ons
          </Link>
        ) : (
          <button 
            disabled
            className="inline-flex items-center gap-2 px-6 py-3 bg-gray-300 text-gray-500 rounded-lg cursor-not-allowed opacity-60"
            title={blockMessage}
          >
            <Package className="w-5 h-5" />
            ซื้อ Storage Add-ons
          </button>
        )}
      </div>
    </div>
  );

  const renderCustomDomain = () => (
    <div className="bg-white rounded-2xl p-6 shadow-xl">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-pink-600 rounded-xl flex items-center justify-center">
          <Globe className="w-6 h-6 text-white" />
        </div>
        <div>
          <h2 className="text-2xl font-bold text-textDark">Custom Domain</h2>
          <p className="text-sm text-textLight">ใช้โดเมนของคุณเอง</p>
        </div>
      </div>
      
      <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl p-6 border border-blue-200">
        <div className="flex items-center gap-2 mb-4">
          <div className="flex-1 bg-white rounded-lg px-4 py-2 border border-blue-200">
            <span className="text-sm text-blue-600 font-mono">
              {user?.username ? `${user.username}.edumi.co` : 'yourname.edumi.co'}
            </span>
          </div>
        </div>
        {hasAccess ? (
          <Link 
            to="/instructor/settings"
            className="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Globe className="w-5 h-5" />
            ตั้งค่า Custom Domain
          </Link>
        ) : (
          <button 
            disabled
            className="inline-flex items-center gap-2 px-6 py-3 bg-gray-300 text-gray-500 rounded-lg cursor-not-allowed opacity-60"
            title={blockMessage}
          >
            <Globe className="w-5 h-5" />
            ตั้งค่า Custom Domain
          </button>
        )}
      </div>
    </div>
  );

  const renderSlaSupport = () => (
    <div className="bg-white rounded-2xl p-6 shadow-xl">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-12 h-12 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-xl flex items-center justify-center">
          <Shield className="w-6 h-6 text-white" />
        </div>
        <div>
          <h2 className="text-2xl font-bold text-textDark">Priority Support & SLA</h2>
          <p className="text-sm text-textLight">การสนับสนุนพิเศษ</p>
        </div>
      </div>
      
      <div className="space-y-3">
        <div className="flex items-center gap-3 bg-green-50 rounded-lg p-4">
          <div className="w-2 h-2 bg-green-500 rounded-full" />
          <span>99.9% Uptime Guarantee</span>
        </div>
        <div className="flex items-center gap-3 bg-green-50 rounded-lg p-4">
          <div className="w-2 h-2 bg-green-500 rounded-full" />
          <span>24/7 Priority Support</span>
        </div>
        <div className="flex items-center gap-3 bg-green-50 rounded-lg p-4">
          <div className="w-2 h-2 bg-green-500 rounded-full" />
          <span>Dedicated Account Manager</span>
        </div>
      </div>
    </div>
  );

  const renderModeratorManagement = () => (
    <div>
      <ModeratorManagement instructorId={user.uid} tier={user.plan || 'free'} />
    </div>
  );

  const renderAnalytics = () => {
    const [loading, setLoading] = useState(true);
    const [range, setRange] = useState<AnalyticsRange>("30d");
    const [analyticsStats, setAnalyticsStats] = useState<InstructorAnalyticsSummary>({
      totalCourses: 0,
      totalStudents: 0,
      totalRevenue: 0,
      avgCompletionRate: 0,
      topCoursesByRevenue: [],
      topCoursesByStudents: [],
    });
    const [courseStats, setCourseStats] = useState<CourseAnalytics[]>([]);
    const [revenueSeries, setRevenueSeries] = useState<RevenuePoint[]>([]);
    const [completionSeries, setCompletionSeries] = useState<CompletionPoint[]>([]);
    const [topCourseSales, setTopCourseSales] = useState<TopCourseSalesPoint[]>([]);

    const rangeLabel = useMemo(() => {
      if (range === "7d") return "7 วัน";
      if (range === "30d") return "30 วัน";
      if (range === "90d") return "90 วัน";
      return "ทั้งหมด";
    }, [range]);

    const hasRevenueData = useMemo(() => {
      return revenueSeries.some((p) => Number(p.revenue) > 0);
    }, [revenueSeries]);

    const hasCompletionData = useMemo(() => {
      return completionSeries.some((p) => Number(p.completionRate) > 0);
    }, [completionSeries]);

    const hasTopSalesData = useMemo(() => {
      return topCourseSales.some((p) => Number(p.revenue) > 0 || Number(p.sales) > 0);
    }, [topCourseSales]);

    const formatXAxisLabel = useMemo(() => {
      if (range === "all") {
        return (value: any) => String(value);
      }
      return (value: any) => {
        const s = String(value);
        const parts = s.split("-");
        if (parts.length === 3) return `${parts[1]}/${parts[2]}`;
        return s;
      };
    }, [range]);

    useEffect(() => {
      async function loadAnalytics() {
        if (!user) return;

        try {
          setLoading(true);
          const { summary, courseStats, revenueSeries, completionSeries, topCourseSales } =
            await loadInstructorAnalytics(user.uid, range);
          setAnalyticsStats(summary);
          setCourseStats(courseStats);
          setRevenueSeries(revenueSeries);
          setCompletionSeries(completionSeries);
          setTopCourseSales(topCourseSales);
          setLoading(false);
        } catch (error) {
          console.error("Error loading analytics:", error);
          setLoading(false);
        }
      }

      loadAnalytics();
    }, [user, range]);

    if (loading) {
      return (
        <div className="flex items-center justify-center py-20">
          <div className="text-center">
            <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-textLight">กำลังโหลดข้อมูล Analytics...</p>
          </div>
        </div>
      );
    }

    return (
      <div className="space-y-6">
        {/* Header */}
        <div className="bg-white rounded-2xl p-6 shadow-xl">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
            <div>
              <h2 className="text-2xl font-bold text-textDark mb-2">Analytics</h2>
              <p className="text-textLight">ภาพรวมสถิติคอร์สของคุณ • ช่วงเวลา: {rangeLabel}</p>
            </div>

            <div className="flex items-center gap-2">
              <span className="text-sm text-textLight">ช่วงเวลา</span>
              <select
                value={range}
                onChange={(e) => setRange(e.target.value as AnalyticsRange)}
                className="px-4 py-2 rounded-xl border-2 border-gray-200 focus:border-blue-600 focus:outline-none transition-colors bg-white"
              >
                <option value="7d">7 วันล่าสุด</option>
                <option value="30d">30 วันล่าสุด</option>
                <option value="90d">90 วันล่าสุด</option>
                <option value="all">ทั้งหมด</option>
              </select>
            </div>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <StatCard
            title="จำนวนคอร์ส"
            value={analyticsStats.totalCourses}
            icon={<BookOpen className="w-6 h-6" />}
            gradient="from-blue-500 to-cyan-500"
          />
          <StatCard
            title="นักเรียนทั้งหมด"
            value={analyticsStats.totalStudents}
            icon={<Users className="w-6 h-6" />}
            gradient="from-blue-500 to-pink-500"
          />
          <StatCard
            title="รายได้รวม"
            value={`฿${analyticsStats.totalRevenue.toLocaleString()}`}
            icon={<DollarSign className="w-6 h-6" />}
            gradient="from-green-500 to-emerald-500"
          />
          <StatCard
            title="อัตราจบคอร์สเฉลี่ย"
            value={`${analyticsStats.avgCompletionRate}%`}
            icon={<Target className="w-6 h-6" />}
            gradient="from-blue-500 to-red-500"
          />
        </div>

        {/* Charts */}
        <div>
          <h3 className="text-xl font-bold text-textDark mb-4 flex items-center gap-2">
            <TrendingUp className="w-6 h-6 text-blue-600" />
            Trends
          </h3>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
            <Card>
              <div className="flex items-center justify-between mb-4">
                <h4 className="font-bold text-textDark">รายได้ ({rangeLabel})</h4>
                <div className="text-sm text-textLight">THB</div>
              </div>

              <div className="h-[280px]">
                {hasRevenueData ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart
                      data={revenueSeries}
                      margin={{ top: 10, right: 16, left: 0, bottom: 0 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                      <XAxis
                        dataKey="date"
                        tickFormatter={formatXAxisLabel as any}
                        minTickGap={12}
                      />
                      <YAxis
                        tickFormatter={(v) => `฿${Number(v).toLocaleString()}`}
                        width={72}
                      />
                      <Tooltip
                        formatter={(value: any) => [
                          `฿${Number(value).toLocaleString()}`,
                          "รายได้",
                        ]}
                        labelFormatter={(label: any) => `วันที่: ${label}`}
                      />
                      <Legend />
                      <Line
                        type="monotone"
                        dataKey="revenue"
                        name="รายได้"
                        stroke="#10B981"
                        strokeWidth={3}
                        dot={false}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="h-full flex flex-col items-center justify-center text-center px-6">
                    <DollarSign className="w-16 h-16 text-gray-300 mb-2" />
                    <p className="font-semibold text-textDark">ยังไม่มีรายได้ในช่วงเวลานี้</p>
                    <p className="text-sm text-textLight mt-1">
                      ระบบจะนับจากรายการชำระเงินที่มีสถานะ completed
                    </p>
                  </div>
                )}
              </div>
            </Card>

            <Card>
              <div className="flex items-center justify-between mb-4">
                <h4 className="font-bold text-textDark">Completion Trend ({rangeLabel})</h4>
                <div className="text-sm text-textLight">%</div>
              </div>

              <div className="h-[280px]">
                {hasCompletionData ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart
                      data={completionSeries}
                      margin={{ top: 10, right: 16, left: 0, bottom: 0 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                      <XAxis
                        dataKey="date"
                        tickFormatter={formatXAxisLabel as any}
                        minTickGap={12}
                      />
                      <YAxis
                        domain={[0, 100]}
                        tickFormatter={(v) => `${v}%`}
                        width={48}
                      />
                      <Tooltip
                        formatter={(value: any) => [`${Number(value)}%`, "อัตราจบคอร์ส"]}
                        labelFormatter={(label: any) => `วันที่: ${label}`}
                      />
                      <Legend />
                      <Line
                        type="monotone"
                        dataKey="completionRate"
                        name="อัตราจบคอร์ส"
                        stroke="#6366F1"
                        strokeWidth={3}
                        dot={false}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="h-full flex flex-col items-center justify-center text-center px-6">
                    <Target className="w-16 h-16 text-gray-300 mb-2" />
                    <p className="font-semibold text-textDark">ยังไม่มีข้อมูล Completion Trend</p>
                    <p className="text-sm text-textLight mt-1">
                      กราฟนี้ใช้ข้อมูลจาก progress ที่มี completedAt
                    </p>
                  </div>
                )}
              </div>
            </Card>
          </div>

          <Card>
            <div className="flex items-center justify-between mb-4">
              <h4 className="font-bold text-textDark">ยอดขายคอร์สยอดนิยม</h4>
              <div className="text-sm text-textLight">Top 7</div>
            </div>

            <div className="h-[320px]">
              {hasTopSalesData ? (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={topCourseSales}
                    margin={{ top: 10, right: 16, left: 0, bottom: 24 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                    <XAxis
                      dataKey="name"
                      interval={0}
                      angle={-12}
                      textAnchor="end"
                      height={60}
                    />
                    <YAxis
                      yAxisId="left"
                      tickFormatter={(v) => `฿${Number(v).toLocaleString()}`}
                      width={72}
                    />
                    <YAxis
                      yAxisId="right"
                      orientation="right"
                      tickFormatter={(v) => `${v}`}
                      width={48}
                    />
                    <Tooltip
                      formatter={(value: any, name: any) => {
                        if (name === "revenue")
                          return [`฿${Number(value).toLocaleString()}`, "รายได้"];
                        return [Number(value), "ยอดขาย"];
                      }}
                    />
                    <Legend />
                    <Bar
                      yAxisId="left"
                      dataKey="revenue"
                      name="รายได้"
                      fill="#06B6D4"
                      radius={[8, 8, 0, 0]}
                    />
                    <Bar
                      yAxisId="right"
                      dataKey="sales"
                      name="ยอดขาย"
                      fill="#EC4899"
                      radius={[8, 8, 0, 0]}
                    />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-full flex flex-col items-center justify-center text-center px-6">
                  <BookOpen className="w-16 h-16 text-gray-300 mb-2" />
                  <p className="font-semibold text-textDark">ยังไม่มีข้อมูลยอดขายในช่วงเวลานี้</p>
                  <p className="text-sm text-textLight mt-1">
                    ระบบจะแสดง Top คอร์สเมื่อมีรายการชำระเงิน completed
                  </p>
                </div>
              )}
            </div>
          </Card>
        </div>

        {/* Course Performance */}
        <div>
          <h3 className="text-xl font-bold text-textDark mb-4">
            ประสิทธิภาพแต่ละคอร์ส
          </h3>

          {courseStats.length === 0 ? (
            <Card>
              <p className="text-textLight text-center py-8">
                ยังไม่มีคอร์ส เริ่มสร้างคอร์สแรกของคุณเลย!
              </p>
            </Card>
          ) : (
            <div className="space-y-4">
              {courseStats.map((course) => (
                <Card key={course.id}>
                  <div className="flex items-center gap-4">
                    <img
                      src={course.thumbnail}
                      alt={course.title}
                      className="w-20 h-20 rounded-xl object-cover"
                    />
                    <div className="flex-1">
                      <h4 className="font-bold text-lg text-textDark mb-2">
                        {course.title}
                      </h4>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        <div>
                          <p className="text-xs text-textLight">นักเรียน</p>
                          <p className="font-semibold text-textDark">
                            {course.totalStudents} คน
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-textLight">รายได้</p>
                          <p className="font-semibold text-green-600">
                            ฿{course.totalRevenue.toLocaleString()}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-textLight">บทเรียน</p>
                          <p className="font-semibold text-textDark">
                            {course.totalLessons} บท
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-textLight">อัตราจบคอร์ส</p>
                          <p className="font-semibold text-blue-600">
                            {course.completionRate}%
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="mt-4">
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className="bg-gradient-to-r from-blue-500 to-cyan-500 h-2 rounded-full transition-all duration-500"
                        style={{ width: `${course.completionRate}%` }}
                      ></div>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </div>

        {/* Top Courses */}
        <div>
          <h3 className="text-xl font-bold text-textDark mb-4 flex items-center gap-2">
            <Trophy className="w-6 h-6 text-yellow-500" />
            Top Courses
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Card>
              <h4 className="font-bold text-textDark mb-4">Top รายได้</h4>
              {analyticsStats.topCoursesByRevenue.length === 0 ? (
                <p className="text-sm text-textLight">ยังไม่มีข้อมูลรายได้ในช่วงเวลานี้</p>
              ) : (
                <div className="space-y-3">
                  {analyticsStats.topCoursesByRevenue.map((c, idx) => (
                    <div key={c.courseId} className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-gradient-to-r from-blue-500 to-cyan-500 text-white flex items-center justify-center font-bold">
                          {idx + 1}
                        </div>
                        <div className="min-w-0">
                          <p className="font-semibold text-textDark truncate max-w-[260px]">
                            {c.title}
                          </p>
                        </div>
                      </div>
                      <p className="font-bold text-green-600">฿{c.revenue.toLocaleString()}</p>
                    </div>
                  ))}
                </div>
              )}
            </Card>

            <Card>
              <h4 className="font-bold text-textDark mb-4">Top นักเรียน</h4>
              {analyticsStats.topCoursesByStudents.length === 0 ? (
                <p className="text-sm text-textLight">ยังไม่มีข้อมูลนักเรียนในช่วงเวลานี้</p>
              ) : (
                <div className="space-y-3">
                  {analyticsStats.topCoursesByStudents.map((c, idx) => (
                    <div key={c.courseId} className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-pink-600 text-white flex items-center justify-center font-bold">
                          {idx + 1}
                        </div>
                        <div className="min-w-0">
                          <p className="font-semibold text-textDark truncate max-w-[260px]">
                            {c.title}
                          </p>
                        </div>
                      </div>
                      <p className="font-bold text-textDark">{c.students} คน</p>
                    </div>
                  ))}
                </div>
              )}
            </Card>
          </div>
        </div>

        {/* Insights */}
        <div>
          <h3 className="text-xl font-bold text-textDark mb-4 flex items-center gap-2">
            <Lightbulb className="w-6 h-6 text-yellow-500" />
            Insights
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Card>
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-cyan-500 rounded-lg flex items-center justify-center text-white flex-shrink-0">
                  <Rocket className="w-5 h-5" />
                </div>
                <div>
                  <h4 className="font-bold text-textDark mb-1">
                    เพิ่มบทเรียนใหม่
                  </h4>
                  <p className="text-sm text-textLight">
                    คอร์สที่มีบทเรียนมากกว่า 10 บท มีอัตราจบคอร์สสูงกว่า 30%
                  </p>
                </div>
              </div>
            </Card>

            <Card>
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 bg-gradient-to-r from-emerald-500 to-cyan-600 rounded-lg flex items-center justify-center text-white flex-shrink-0">
                  <TrendingUp className="w-5 h-5" />
                </div>
                <div>
                  <h4 className="font-bold text-textDark mb-1">
                    อัปเดตเนื้อหา
                  </h4>
                  <p className="text-sm text-textLight">
                    คอร์สที่อัปเดตเนื้อหาสม่ำเสมอมีนักเรียนเพิ่มขึ้น 45%
                  </p>
                </div>
              </div>
            </Card>

            <Card>
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Target className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <h4 className="font-bold text-textDark mb-1">
                    เพิ่มแบบฝึกหัด
                  </h4>
                  <p className="text-sm text-textLight">
                    คอร์สที่มีแบบฝึกหัดมีอัตราจบคอร์สสูงกว่า 25%
                  </p>
                </div>
              </div>
            </Card>

            <Card>
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 bg-green-500 rounded-lg flex items-center justify-center text-white flex-shrink-0">
                  <MessageCircle className="w-5 h-5" />
                </div>
                <div>
                  <h4 className="font-bold text-textDark mb-1">
                    ตอบคำถามนักเรียน
                  </h4>
                  <p className="text-sm text-textLight">
                    การตอบคำถามภายใน 24 ชม. เพิ่มความพึงพอใจ 60%
                  </p>
                </div>
              </div>
            </Card>
          </div>
        </div>
      </div>
    );
  };

  // Render content based on active section
  switch (activeSection) {
    case 'overview':
      return renderOverview();
    case 'revenue':
      return renderRevenue();
    case 'courses':
      return renderCourses();
    case 'students':
      return renderStudents();
    case 'storage':
      return renderStorage();
    case 'analytics':
      return renderAnalytics();
    case 'approve-students':
      return renderApproveStudents();
    case 'video-reports':
      return <VideoReportsInstructor />;
    case 'student-reports':
      return renderStudentReports();
    case 'discounts':
      return renderDiscounts();
    case 'moderator-management':
      return renderModeratorManagement();
    case 'team-management':
      return renderTeamManagement();
    case 'white-label':
      return renderWhiteLabel();
    case 'storage-addons':
      return renderStorageAddons();
    case 'custom-domain':
      return renderCustomDomain();
    case 'sla-support':
      return renderSlaSupport();
    default:
      return renderOverview();
  }
}
